// ============================================================
// 티켓 투 라이드 — Konva 보드 본문 (SEQ=10146 UX 본문)
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §3-3 / §3-A / §3-2
//
//   변경 사항 vs SEQ=10143 stub (직선 노선만):
//     1) 노선 길이별 N개 정사각형 슬롯 (Days of Wonder 원작 시각 차용)
//     2) Hover 강조 + 길이/색 툴팁
//     3) 점유 시 사각형 슬롯에 본인 색 fill (lane offset 보존)
//     4) 더블 노선 lane 1·2 평행 offset
//     5) 핀치/휠 줌 (카탄 CatanBoard 패턴 차용, scaleBy = 1.1)
//     6) onCityClick 본인 티켓 src/dst 강조 토글 (외부 highlightCityIds prop)
//     7) onRouteClick 액션 (점유된 lane은 비활성)
//     8) Highlight 도시 ring (티켓 src/dst 강조)
// ============================================================

import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import {
  Stage as _Stage,
  Layer,
  Circle,
  Text,
  Line,
  Group,
  Rect,
  Image as KonvaImage,
} from 'react-konva'
import type Konva from 'konva'
import {
  TtrCity,
  TtrRoute,
  TtrRouteClaim,
  TtrPlayer,
  TtrRecentClaim,
} from '../store/ticket'
import { calcLongestPath } from '../util/ttrGraph'

// react-konva 18 children FC workaround (CatanBoard 패턴)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Stage = _Stage as any

// ── 노선 8색 + GRAY + WILD 매핑 (SoT §3-A v2 — 보드 톤 fine-tune, SEQ=10148) ───
// 카드 톤은 TtrPlayTicket.css의 --ttr-card-* 변수 (광택 ↑), 보드는 인쇄 톤 (채도 약간 ↓)
// catch-B fix (SEQ=10195): BLACK 보드 톤 #1C1C1C → #34495E (블루 차콜)
//   USA 지도 SVG 배경(#0f172a~#1a2742 네이비) 위에서 #1C1C1C 묻힘 → lift로 대비 확보.
//   카드 톤 BLACK #2C3E50 (TtrPlayTicket.css --ttr-card-black) 보존 — §3-A 보드/카드 분리 정합.
const ROUTE_COLOR_HEX: Record<string, string> = {
  GRAY:   '#9E9E9E',
  RED:    '#C0392B',
  ORANGE: '#E07B1F',
  YELLOW: '#E8C547',
  GREEN:  '#27923F',
  BLUE:   '#1F4E97',
  PINK:   '#C84A8E',
  WHITE:  '#ECEFF1',
  BLACK:  '#34495E',
}

// ── 좌석 5색 (점유 시 노선 색 덮어쓰기) — SoT §3-A v2 (YELLOW v2 #D4A91C) ─
const PLAYER_COLOR_HEX: Record<string, string> = {
  RED:    '#C81E1E',
  BLUE:   '#1957B5',
  GREEN:  '#2E8E3E',
  YELLOW: '#D4A91C',
  BLACK:  '#222222',
}

// ── catch-A fix: 라벨 anchor override (SEQ=10195)
//   짧은 노선(LENGTH≤2)으로 붙어 있어 라벨이 노선에 overlap되는 L1/L2 도시에 한해
//   노드 기준 anchor를 'top'으로 오버라이드 — 라벨을 노드 위쪽으로 이동.
//   key = CITY_ID, value = { dx, dy } 오프셋 (노드 중심 기준, px)
//   Oklahoma City(18): L1 Hub, 동쪽 리틀록(22)과 LENGTH=2 수평 노선 → 라벨 우→위 이동
const LABEL_ANCHOR_OVERRIDE = new Map<number, { dx: number; dy: number }>([
  [18, { dx: -28, dy: -22 }], // 오클라호마시티 L1 — 위쪽 배치 (노드 위 16px 여유 + 라벨 폭 절반 좌)
])

// ── USA 지도 배경 자산 경로 (Public Domain 자체 생성, SEQ=10149) ─
// public/ 하위 정적 자산 — CRA `process.env.PUBLIC_URL` 권장이나 nginx 직접 마운트라 절대 경로 사용
const USA_MAP_SVG_URL = '/assets/ttr/usa-map.svg'

// ── Props ────────────────────────────────────────────────
export interface TtrBoardProps {
  width:    number
  height:   number
  cities:   TtrCity[]
  routes:   TtrRoute[]
  claims:   TtrRouteClaim[]
  players:  TtrPlayer[]
  /** 클릭 가능한 lane 후보 (clientside 필터, isMyTurn 등) — undefined면 무제한 */
  clickableLanes?:  (route: TtrRoute) => boolean
  /** 본인 티켓 강조 도시 IDs (option) */
  highlightCityIds?: number[]
  /** SEQ=10200 STEP=1 (catch #11) — 최근 점유 lane (1.5초). 없으면 null. ring + +N popover 트리거. */
  recentClaim?:     TtrRecentClaim | null
  /** SEQ=10211 (catch #1) — 모바일 viewport 여부. true 시 slot Rect hitStrokeWidth 확장으로 노선 mistap 해소. */
  isMobile?:        boolean
  /** SEQ=10211 STEP=8 — 줌 controls 렌더 여부. false 시 fixed button lingering 차단 (핸드 탭에서 우측 패널 button 클릭 차단 catch fix). */
  showZoomControls?: boolean
  onRouteClick?:    (route: TtrRoute) => void
  onCityClick?:     (cityId: number) => void
}

// ── 노선 칸 렌더 헬퍼 ────────────────────────────────────
/**
 * 두 점 a, b 사이를 length 칸의 사각형 슬롯으로 분할.
 * 각 슬롯은 노선 방향 16px × 수직 8px 크기, 회전 정렬.
 * Phase 0 디자이너 fine-tune 시 sizing 조정 (§3-A 디자이너 라운드).
 */
interface SlotRect {
  cx:       number
  cy:       number
  rotation: number  // deg, 노선 방향
}

function calcSlotRects(
  ax: number, ay: number, bx: number, by: number,
  length: number,
  laneOffset: number,  // lane 1·2 평행 offset
): SlotRect[] {
  const dx = bx - ax
  const dy = by - ay
  const len = Math.hypot(dx, dy) || 1
  // 수직 단위 (-dy/len, dx/len)
  const px = -(dy / len)
  const py =  (dx / len)
  // lane 평행 offset 적용한 가운데 시작·끝점
  const sx = ax + px * laneOffset
  const sy = ay + py * laneOffset
  const ex = bx + px * laneOffset
  const ey = by + py * laneOffset
  // 양끝 도시 노드(반경 8) 만큼 마진 — 양쪽 0.18 비율
  const inset = 0.18
  const startX = sx + (ex - sx) * inset
  const startY = sy + (ey - sy) * inset
  const endX   = sx + (ex - sx) * (1 - inset)
  const endY   = sy + (ey - sy) * (1 - inset)

  const rotation = Math.atan2(endY - startY, endX - startX) * 180 / Math.PI
  const rects: SlotRect[] = []
  for (let i = 0; i < length; i++) {
    const t = (i + 0.5) / length
    rects.push({
      cx: startX + (endX - startX) * t,
      cy: startY + (endY - startY) * t,
      rotation,
    })
  }
  return rects
}

// ════════════════════════════════════════════════════════
// TtrBoard
// ════════════════════════════════════════════════════════
const TtrBoard: React.FC<TtrBoardProps> = ({
  width,
  height,
  cities,
  routes,
  claims,
  players,
  clickableLanes,
  highlightCityIds,
  recentClaim,
  isMobile,
  showZoomControls,
  onRouteClick,
  onCityClick,
}) => {
  // ── SEQ=10211 STEP=14 (사용자 catch "보드 영역 우측·아래 빈공간 많음"):
  //   wrap 컨테이너 크기 동적 측정 → Stage canvas width/height 자동 fit.
  //   props.width/height는 initial fallback (1024×768 base).
  //   ResizeObserver로 wrap 크기 변화 시 boardSize 갱신 → 도시 좌표 및 Stage 자동 재계산.
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [boardSize, setBoardSize] = useState<{ w: number; h: number }>({ w: width, h: height })
  useEffect(() => {
    const el = wrapperRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect
      if (rect && rect.width > 0 && rect.height > 0) {
        setBoardSize({ w: rect.width, h: rect.height })
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // ── 도시 픽셀 좌표 캐시 (boardSize 의존, wrap stretch fit) ──
  const cityPixels = useMemo(() => {
    const map = new Map<number, { x: number; y: number; name: string }>()
    cities.forEach(c => {
      map.set(c.cityId, {
        x:    c.displayX * boardSize.w,
        y:    (1 - c.displayY) * boardSize.h,
        name: c.name,
      })
    })
    return map
  }, [cities, boardSize.w, boardSize.h])

  // ── 도시 hierarchy (SoT §3-A v2.1 + §3-A-detail ⑧ IMPORTANCE 컬럼 SEQ=10186)
  //   L1 (Hub):    굵게, 14px, 외곽선
  //   L2 (주요):    보통, 12px
  //   L3 (소도시):  얇게, 11px, opacity 0.85
  //
  //   우선순위:
  //     1) cities[].importance (TTR_CITY.IMPORTANCE 정적 마스터, SEQ=10186 ③)
  //     2) fallback = 본 회차 점유 노선 connections 기반 동적 카운트 (SEQ=10149 v1)
  //
  //   IMPORTANCE 컬럼이 BG fallback 으로도 안전하게 동작하도록, 정적 마스터에 importance 가
  //   존재하지 않으면 종전 분류 알고리즘을 유지한다.
  const cityHierarchy = useMemo(() => {
    const result = new Map<number, 1 | 2 | 3>()
    // 1차 — IMPORTANCE 컬럼 사용 (있을 때만)
    type CityWithImportance = TtrCity & { importance?: number }
    const hasImportance = cities.some(c => typeof (c as CityWithImportance).importance === 'number')
    if (hasImportance) {
      cities.forEach(c => {
        const imp = (c as CityWithImportance).importance ?? 3
        const tier: 1 | 2 | 3 = imp === 1 ? 1 : (imp === 2 ? 2 : 3)
        result.set(c.cityId, tier)
      })
      return result
    }
    // 2차 fallback — pair 기반 연결 카운트 (double 노선은 인접 city로 1회만)
    const conn = new Map<number, number>()
    const seenPair = new Set<string>()
    routes.forEach(r => {
      const a = Math.min(r.cityAId, r.cityBId)
      const b = Math.max(r.cityAId, r.cityBId)
      const k = `${a}-${b}`
      if (seenPair.has(k)) return
      seenPair.add(k)
      conn.set(r.cityAId, (conn.get(r.cityAId) ?? 0) + 1)
      conn.set(r.cityBId, (conn.get(r.cityBId) ?? 0) + 1)
    })
    cities.forEach(c => {
      const n = conn.get(c.cityId) ?? 0
      result.set(c.cityId, n >= 6 ? 1 : (n >= 4 ? 2 : 3))
    })
    return result
  }, [cities, routes])

  // ── 플레이어별 longest path 계산 (SEQ=10186 fine-tune ①)
  //   전원 표시 (관전 명료성) — 노선 점유 정보는 모두 공개이므로 정보 비대칭 영향 없음.
  //   결과: Map<userCode, { length, laneKeys }>
  //   각 lane 의 색은 owner 좌석 색 + opacity overlay 로 보드 위 ring 렌더.
  //   동률 시 length 최대값 플레이어 모두 trophy 강조.
  const longestPathByUser = useMemo(() => {
    const m = new Map<number, { length: number; laneKeys: Set<string> }>()
    players.forEach(pl => {
      const res = calcLongestPath(
        claims.map(c => ({ routeId: c.routeId, laneNo: c.laneNo, ownerUserCode: c.ownerUserCode })),
        routes,
        pl.userCode,
      )
      m.set(pl.userCode, res)
    })
    return m
  }, [claims, routes, players])

  // 트로피 보유자 (동률 시 다수) — laneKeys ring 굵게 강조용
  const trophyHolders = useMemo(() => {
    let best = 0
    longestPathByUser.forEach(v => { if (v.length > best) best = v.length })
    if (best === 0) return new Set<number>()
    const holders = new Set<number>()
    longestPathByUser.forEach((v, uc) => { if (v.length === best) holders.add(uc) })
    return holders
  }, [longestPathByUser])

  // 본인 (또는 임의 user) 색 조회 헬퍼
  const playerColorByCode = useMemo(() => {
    const m = new Map<number, string>()
    players.forEach(pl => m.set(pl.userCode, pl.color))
    return m
  }, [players])

  // ── 도시 hover 상태 (SEQ=10186 fine-tune ②)
  //   hoveredCityId 인접 lane(routesMaster 기반) 모두 highlight ring.
  //   모바일: tap-and-hold 0.4s (TouchStart 타이머 등록 → 0.4s 후 set, release/이동 시 해제)
  const [hoveredCityId, setHoveredCityId] = useState<number | null>(null)
  const cityHoldTimerRef = useRef<number | null>(null)
  const clearCityHoldTimer = useCallback(() => {
    if (cityHoldTimerRef.current !== null) {
      window.clearTimeout(cityHoldTimerRef.current)
      cityHoldTimerRef.current = null
    }
  }, [])
  // hover 시 인접 lane key 집합
  const hoveredAdjacentLaneKeys = useMemo(() => {
    if (hoveredCityId === null) return new Set<string>()
    const s = new Set<string>()
    routes.forEach(r => {
      if (r.cityAId === hoveredCityId || r.cityBId === hoveredCityId) {
        s.add(`${r.routeId}:${r.laneNo}`)
      }
    })
    return s
  }, [hoveredCityId, routes])

  // ── USA 지도 배경 이미지 로드 (SoT §3-A v2.1, SEQ=10149)
  //   public/assets/ttr/usa-map.svg — Konva Image 레이어(Layer 0, z-index 가장 아래)로 배치
  //   listening=false → 배경은 클릭 이벤트 무시
  const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null)
  useEffect(() => {
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.src = USA_MAP_SVG_URL
    img.onload = () => setMapImage(img)
    img.onerror = (e) => {
      // 자산 부재/네트워크 실패 시 graceful — 배경 없이 보드만 렌더
      console.warn('[TtrBoard] USA 지도 배경 로드 실패 (graceful, 기본 그라데이션 유지)', e)
    }
  }, [])

  // ── 점유 lane → 색 매핑 ─────────────────────────────────
  const claimColorMap = useMemo(() => {
    const map = new Map<string, string>()  // key = `${routeId}:${laneNo}`
    claims.forEach(cl => {
      const owner = players.find(pl => pl.userCode === cl.ownerUserCode)
      const ownerColor = owner?.color ?? cl.color
      map.set(`${cl.routeId}:${cl.laneNo}`, PLAYER_COLOR_HEX[ownerColor] ?? '#888888')
    })
    return map
  }, [claims, players])

  // ── Hover 상태 (마우스 호버 lane) ─────────────────────────
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)

  // ── SEQ=10200 STEP=1 (catch #11) — 최근 점유 lane 진행도 (0..1, 1.5초) ─
  //   requestAnimationFrame 루프로 매 프레임 진행도 갱신 → ring opacity·strokeWidth·popover y/opacity 보간.
  //   recentClaim.ts 변동 시 hook 재시작 (연속 클레임 시 직전 ring 강제 종료).
  const [recentClaimProgress, setRecentClaimProgress] = useState<number>(0)
  const recentClaimRafRef = useRef<number | null>(null)
  useEffect(() => {
    if (!recentClaim) {
      setRecentClaimProgress(0)
      if (recentClaimRafRef.current !== null) {
        cancelAnimationFrame(recentClaimRafRef.current)
        recentClaimRafRef.current = null
      }
      return
    }
    const startTs  = recentClaim.ts
    const duration = 1500  // 1.5초
    const tick = () => {
      const elapsed = Date.now() - startTs
      const p = Math.min(1, Math.max(0, elapsed / duration))
      setRecentClaimProgress(p)
      if (p < 1) {
        recentClaimRafRef.current = requestAnimationFrame(tick)
      } else {
        recentClaimRafRef.current = null
      }
    }
    recentClaimRafRef.current = requestAnimationFrame(tick)
    return () => {
      if (recentClaimRafRef.current !== null) {
        cancelAnimationFrame(recentClaimRafRef.current)
        recentClaimRafRef.current = null
      }
    }
    // recentClaim 객체 자체가 store에서 신규 객체로 재할당되므로 reference 비교만으로 routeId/laneNo/ts 변동 trigger 정합.
  }, [recentClaim])

  // ── 줌 / 팬 (휠 + 핀치) ─────────────────────────────────
  const stageRef = useRef<Konva.Stage | null>(null)
  const [scale,    setScale]    = useState<number>(1)
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const handleWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault()
    const stage = stageRef.current
    if (!stage) return
    const oldScale = scale
    const pointer = stage.getPointerPosition()
    if (!pointer) return
    const scaleBy = 1.1
    const direction = e.evt.deltaY > 0 ? -1 : 1
    const newScale = Math.max(0.5, Math.min(3, direction > 0 ? oldScale * scaleBy : oldScale / scaleBy))
    const mousePointTo = {
      x: (pointer.x - position.x) / oldScale,
      y: (pointer.y - position.y) / oldScale,
    }
    setScale(newScale)
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    })
  }, [scale, position])

  // 핀치 줌 (카탄 SEQ=10080 패턴 축약)
  // touch 1개 = 팬 / 2개 = pinch
  const pinchRef = useRef<{
    active:        boolean
    startDist:     number
    startScale:    number
    centerStart:   { x: number; y: number }
    originStart:   { x: number; y: number }
  }>({
    active: false, startDist: 0, startScale: 1,
    centerStart: { x: 0, y: 0 }, originStart: { x: 0, y: 0 },
  })

  const handleTouchMove = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
    const stage = stageRef.current
    if (!stage) return
    const touches = e.evt.touches
    if (touches.length === 2) {
      e.evt.preventDefault()
      const t0 = touches[0]
      const t1 = touches[1]
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)
      const cx   = (t0.clientX + t1.clientX) / 2
      const cy   = (t0.clientY + t1.clientY) / 2
      const g = pinchRef.current
      if (!g.active) {
        g.active        = true
        g.startDist     = dist
        g.startScale    = scale
        g.centerStart   = { x: cx, y: cy }
        g.originStart   = { ...position }
      } else if (g.startDist > 0) {
        const ratio   = dist / g.startDist
        const ns      = Math.max(0.5, Math.min(3, g.startScale * ratio))
        // pinch 중심 기준 zoom (카탄과 같은 공식)
        const stageBox = stage.container().getBoundingClientRect()
        const px = g.centerStart.x - stageBox.left
        const py = g.centerStart.y - stageBox.top
        const mousePointTo = {
          x: (px - g.originStart.x) / g.startScale,
          y: (py - g.originStart.y) / g.startScale,
        }
        setScale(ns)
        setPosition({
          x: px - mousePointTo.x * ns,
          y: py - mousePointTo.y * ns,
        })
      }
    }
  }, [scale, position])

  const handleTouchEnd = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
    if (e.evt.touches.length < 2) {
      pinchRef.current.active = false
    }
  }, [])

  // ── 더블 클릭 줌 토글 (모바일 보조) ──────────────────────
  const handleDblTap = useCallback((e: Konva.KonvaEventObject<Event>) => {
    const stage = stageRef.current
    if (!stage) return
    const next = scale === 1 ? 1.6 : 1
    const pointer = stage.getPointerPosition()
    if (!pointer) { setScale(next); setPosition({ x: 0, y: 0 }); return }
    const mousePointTo = {
      x: (pointer.x - position.x) / scale,
      y: (pointer.y - position.y) / scale,
    }
    setScale(next)
    setPosition({
      x: pointer.x - mousePointTo.x * next,
      y: pointer.y - mousePointTo.y * next,
    })
    void e
  }, [scale, position])

  // ── SEQ=10211 STEP=5 (사용자 catch "모바일에서 보드 확대/축소 어려움") + STEP=9 (사용자 catch "원래 있던 위치 중앙으로 확대/축소"):
  //   anchor = Stage container의 실측 bounding rect 가운데 픽셀(= 사용자가 보고 있는 viewport 중앙).
  //   기존 Stage 논리 좌표(width/2, height/2 = 1024/2, 768/2) 고정 anchor는 팬·줌 후 viewport 가운데와 다름 → 사용자 인지 "안 보이는 곳이 anchor".
  //   container.getBoundingClientRect()로 실측 width/height 가운데 = container 좌표계 가운데(Konva Stage 픽셀 좌표 정합).
  //   reset = scale=1 + position={0,0} (초기 상태 복원).
  const zoomAtCenter = useCallback((nextScale: number) => {
    const stage = stageRef.current
    if (!stage) return
    const stageBox = stage.container().getBoundingClientRect()
    const cx = stageBox.width  / 2
    const cy = stageBox.height / 2
    const oldScale = scale
    const mousePointTo = {
      x: (cx - position.x) / oldScale,
      y: (cy - position.y) / oldScale,
    }
    const clamped = Math.max(0.5, Math.min(3, nextScale))
    setScale(clamped)
    setPosition({
      x: cx - mousePointTo.x * clamped,
      y: cy - mousePointTo.y * clamped,
    })
  }, [scale, position])
  const zoomIn   = useCallback(() => zoomAtCenter(scale * 1.25), [scale, zoomAtCenter])
  const zoomOut  = useCallback(() => zoomAtCenter(scale / 1.25), [scale, zoomAtCenter])
  const zoomReset = useCallback(() => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }, [])

  // ── 강조 도시 IDs Set ───────────────────────────────────
  const highlightSet = useMemo(() => new Set(highlightCityIds ?? []), [highlightCityIds])

  return (
    <div ref={wrapperRef} className="ttr-board-wrapper" style={{ position: 'relative', width: '100%', height: '100%' }}>
    <Stage
      ref={stageRef}
      width={boardSize.w}
      height={boardSize.h}
      className="ttr-board"
      scaleX={scale}
      scaleY={scale}
      x={position.x}
      y={position.y}
      draggable
      onWheel={handleWheel}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onDblTap={handleDblTap}
    >
      {/* ── 배경 Layer: USA 지도 SVG (z-index 0, 클릭 무시) ─── */}
      <Layer listening={false}>
        {mapImage && (
          <KonvaImage
            image={mapImage}
            x={0}
            y={0}
            width={boardSize.w}
            height={boardSize.h}
            opacity={0.92}
            listening={false}
          />
        )}
      </Layer>

      <Layer>
        {/* ── 노선 (각 lane 별 N개 사각형 슬롯) ────────── */}
        {routes.map(r => {
          const a = cityPixels.get(r.cityAId)
          const b = cityPixels.get(r.cityBId)
          if (!a || !b) return null

          const claimKey  = `${r.routeId}:${r.laneNo}`
          const claimed   = claimColorMap.has(claimKey)
          const baseColor = ROUTE_COLOR_HEX[r.color] ?? '#9E9E9E'
          const slotColor = claimed
            ? (claimColorMap.get(claimKey) ?? '#888888')
            : baseColor
          const hovered   = hoveredKey === claimKey
          const isClickable = !claimed && (!clickableLanes || clickableLanes(r))

          // 더블 lane offset (lane 1 위 / lane 2 아래)
          const laneOffset = r.isDouble ? (r.laneNo === 1 ? 7 : -7) : 0

          const rects = calcSlotRects(a.x, a.y, b.x, b.y, r.length, laneOffset)

          // catch #18 fix (SEQ=10203 STEP=1, 2026-05-26): 점유 endpoint thick line + slot stroke 강화 좌표 계산
          //   lane offset 적용 endpoint (도시 노드 살짝 안쪽 inset 0.08로 노드 가림 방지).
          const _dx = b.x - a.x
          const _dy = b.y - a.y
          const _len = Math.hypot(_dx, _dy) || 1
          const _px = -(_dy / _len)
          const _py =  (_dx / _len)
          const _startX = a.x + _px * laneOffset
          const _startY = a.y + _py * laneOffset
          const _endX   = b.x + _px * laneOffset
          const _endY   = b.y + _py * laneOffset
          const _inset  = 0.08
          const _lineStart = { x: _startX + (_endX - _startX) * _inset, y: _startY + (_endY - _startY) * _inset }
          const _lineEnd   = { x: _startX + (_endX - _startX) * (1 - _inset), y: _startY + (_endY - _startY) * (1 - _inset) }
          const claimedColor = claimed ? (claimColorMap.get(claimKey) ?? '#888888') : null

          return (
            <Group key={`route-${r.routeId}-${r.laneNo}`}>
              {/* 기본 line(slot 사이 배경) — 색 노선 시각 보조 */}
              <Line
                points={[a.x, a.y, b.x, b.y].map((v, i) =>
                  i % 2 === 0
                    ? v + (-( (b.y - a.y) / (Math.hypot(b.x - a.x, b.y - a.y) || 1) )) * laneOffset
                    : v + ( ( (b.x - a.x) / (Math.hypot(b.x - a.x, b.y - a.y) || 1) )) * laneOffset
                )}
                stroke={r.color === 'GRAY' ? '#BDBDBD' : baseColor}
                strokeWidth={r.color === 'WHITE' ? 2 : 1}
                opacity={claimed ? 0 : 0.35}
                dash={r.color === 'GRAY' ? [4, 3] : undefined}
                listening={false}
              />
              {/* catch #18 (SEQ=10203 STEP=1): 점유 lane endpoint thick line overlay
                  - 좌석 색 7px stroke + opacity 0.55 + lineCap round
                  - slot 직전에 그려서 slot이 위에 보이도록 (slot fill 유지 + 두 도시 연결성 강조)
                  - listening=false로 클릭 차단 안 함 */}
              {claimed && (
                <Line
                  points={[_lineStart.x, _lineStart.y, _lineEnd.x, _lineEnd.y]}
                  stroke={claimedColor ?? '#888888'}
                  strokeWidth={7}
                  opacity={0.55}
                  lineCap="round"
                  listening={false}
                />
              )}
              {/* 각 슬롯 사각형 (length 만큼) */}
              {rects.map((rc, idx) => (
                <React.Fragment key={`slot-frag-${r.routeId}-${r.laneNo}-${idx}`}>
                  <Rect
                    key={`slot-${r.routeId}-${r.laneNo}-${idx}`}
                    x={rc.cx}
                    y={rc.cy}
                    offsetX={8}
                    offsetY={4}
                    width={16}
                    height={8}
                    rotation={rc.rotation}
                    fill={slotColor}
                    /* catch #18 (SEQ=10203 STEP=1): 점유 slot stroke 좌석 색 2px (hover 우선) */
                    stroke={
                      hovered && isClickable
                        ? '#FFEB3B'
                        : (claimed
                            ? (claimedColor ?? '#1A1A1A')
                            : (r.color === 'WHITE' ? '#888888' : '#1A1A1A'))
                    }
                    strokeWidth={hovered && isClickable ? 2.5 : (claimed ? 2 : 1)}
                    shadowColor={hovered && isClickable ? '#FFEB3B' : undefined}
                    shadowBlur={hovered && isClickable ? 6 : 0}
                    opacity={claimed ? 1 : 0.95}
                    listening={isClickable}
                    /* SEQ=10211 catch #1: 모바일 mistap 해소 — hitStrokeWidth invisible 확장.
                     * SEQ=10214 STEP=1 (사용자 catch "모바일 인접 lane 겹쳐 선택 불가"):
                     *   - 모바일 22→14 축소 (double route lane offset ±7 = 간격 14px 정합, 인접 lane 겹침 차단)
                     *   - 점유 slot은 hitStrokeWidth=0 명시 (점유=클릭 불요, 옆 미점유 lane 클릭 가로채기 차단)
                     * Konva 권위 hit 영역만 변경(시각 stroke·fill 무영향). */
                    perfectDrawEnabled={false}
                    hitStrokeWidth={claimed ? 0 : (isMobile ? 14 : 12)}
                    onMouseEnter={() => setHoveredKey(claimKey)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onClick={() => onRouteClick && isClickable && onRouteClick(r)}
                    onTap={()  => onRouteClick && isClickable && onRouteClick(r)}
                  />
                  {/* SEQ=10213 STEP=1 (사용자 요청): 점유 slot 기차 차량 줄무늬 overlay (윈도우 2줄)
                      좌석 색 위에 흰색 stripe 2개 (slot 길이방향, 위/아래 평행).
                      slot 크기 16x8 — stripe 12x1.2, offsetY ±2 = 차량 옆면 윈도우 느낌.
                      listening=false — 클릭은 slot이 처리. */}
                  {claimed && (
                    <>
                      <Rect
                        x={rc.cx}
                        y={rc.cy}
                        offsetX={6}
                        offsetY={2.6}
                        width={12}
                        height={1.2}
                        rotation={rc.rotation}
                        fill="rgba(255, 255, 255, 0.9)"
                        listening={false}
                        perfectDrawEnabled={false}
                      />
                      <Rect
                        x={rc.cx}
                        y={rc.cy}
                        offsetX={6}
                        offsetY={-1.4}
                        width={12}
                        height={1.2}
                        rotation={rc.rotation}
                        fill="rgba(255, 255, 255, 0.9)"
                        listening={false}
                        perfectDrawEnabled={false}
                      />
                    </>
                  )}
                </React.Fragment>
              ))}
            </Group>
          )
        })}

        {/* ── ① Longest path overlay ring (SEQ=10186 fine-tune ①) ──
              플레이어별 longest path 에 포함된 lane 슬롯 위에 좌석 색 + opacity 0.55 ring overlay.
              트로피 보유자(동률 다수) lane 은 dash [10,4] 굵게, 일반 path 는 dash [6,4].
              listening=false — 클릭은 슬롯 자체가 처리. */}
        {Array.from(longestPathByUser.entries()).map(([uc, info]) => {
          if (info.length === 0) return null
          const isTrophy = trophyHolders.has(uc)
          const seatColor = PLAYER_COLOR_HEX[playerColorByCode.get(uc) ?? ''] ?? '#888888'
          return Array.from(info.laneKeys).map(key => {
            const [rid, ln] = key.split(':').map(Number)
            const r = routes.find(rr => rr.routeId === rid && rr.laneNo === ln)
            if (!r) return null
            const a = cityPixels.get(r.cityAId)
            const b = cityPixels.get(r.cityBId)
            if (!a || !b) return null
            const laneOffset = r.isDouble ? (r.laneNo === 1 ? 7 : -7) : 0
            const rects = calcSlotRects(a.x, a.y, b.x, b.y, r.length, laneOffset)
            return rects.map((rc, idx) => (
              <Rect
                key={`lp-${uc}-${rid}-${ln}-${idx}`}
                x={rc.cx}
                y={rc.cy}
                offsetX={11}
                offsetY={6}
                width={22}
                height={12}
                rotation={rc.rotation}
                fill={seatColor}
                opacity={0.55}
                stroke={seatColor}
                strokeWidth={isTrophy ? 1.8 : 1.5}
                dash={isTrophy ? [10, 4] : [6, 4]}
                listening={false}
              />
            ))
          })
        })}

        {/* ── ② 도시 hover 인접 lane ring (SEQ=10186 fine-tune ②) ──
              hoveredCityId 가 닿는 lane 마스터 모두에 라임 톤 ring overlay 표시.
              cityHierarchy 라벨/노드 강조와 충돌 없음. listening=false. */}
        {hoveredCityId !== null && Array.from(hoveredAdjacentLaneKeys).map(key => {
          const [rid, ln] = key.split(':').map(Number)
          const r = routes.find(rr => rr.routeId === rid && rr.laneNo === ln)
          if (!r) return null
          const a = cityPixels.get(r.cityAId)
          const b = cityPixels.get(r.cityBId)
          if (!a || !b) return null
          const laneOffset = r.isDouble ? (r.laneNo === 1 ? 7 : -7) : 0
          const rects = calcSlotRects(a.x, a.y, b.x, b.y, r.length, laneOffset)
          return rects.map((rc, idx) => (
            <Rect
              key={`hov-${rid}-${ln}-${idx}`}
              x={rc.cx}
              y={rc.cy}
              offsetX={12}
              offsetY={7}
              width={24}
              height={14}
              rotation={rc.rotation}
              fill="#A8E063"
              opacity={0.40}
              stroke="#A8E063"
              strokeWidth={1}
              listening={false}
            />
          ))
        })}

        {/* ── ③ SEQ=10200 STEP=1 (catch #11) — 최근 점유 lane ring overlay (1.5초) ───
              recentClaim.routeId/laneNo의 모든 슬롯 위에 좌석 색 ring overlay.
              progress 0→1 동안:
                stroke opacity 0.95 → 0.0 (강 → 약 감쇠)
                strokeWidth 5 → 1.2 (선형 감쇠)
                offsetX/Y 확장 +0 → +6 (ring 바깥으로 퍼짐)
              listening=false (클릭 차단 없음). 도시 노드 아래에 배치 (라벨/도시 가리지 않음). */}
        {recentClaim && recentClaimProgress < 1 && (() => {
          const r = routes.find(rr => rr.routeId === recentClaim.routeId && rr.laneNo === recentClaim.laneNo)
          if (!r) return null
          const a = cityPixels.get(r.cityAId)
          const b = cityPixels.get(r.cityBId)
          if (!a || !b) return null
          const laneOffset = r.isDouble ? (r.laneNo === 1 ? 7 : -7) : 0
          const rects = calcSlotRects(a.x, a.y, b.x, b.y, r.length, laneOffset)
          const seatColor = PLAYER_COLOR_HEX[recentClaim.ownerColor] ?? '#FFEB3B'
          const t = recentClaimProgress
          const ringOpacity = 0.95 * (1 - t)
          const ringStroke  = 5 - 3.8 * t        // 5 → 1.2
          const expand      = 6 * t              // 0 → +6 px (ring 확장)
          return rects.map((rc, idx) => (
            <Rect
              key={`recent-${recentClaim.routeId}-${recentClaim.laneNo}-${idx}`}
              x={rc.cx}
              y={rc.cy}
              offsetX={12 + expand}
              offsetY={7 + expand}
              width={24 + expand * 2}
              height={14 + expand * 2}
              rotation={rc.rotation}
              fill={undefined}
              stroke={seatColor}
              strokeWidth={ringStroke}
              opacity={ringOpacity}
              cornerRadius={3}
              listening={false}
            />
          ))
        })()}

        {/* ── 도시 노드 + 라벨 + 강조 ring (폰트 hierarchy §3-A v2.1, hover ring §3-A-detail ⑦) ─── */}
        {cities.map(c => {
          const p = cityPixels.get(c.cityId)
          if (!p) return null
          const highlighted = highlightSet.has(c.cityId)
          const tier = cityHierarchy.get(c.cityId) ?? 3
          const isHovered = hoveredCityId === c.cityId
          // tier별 폰트 명세 (SoT §3-A v2.1)
          const fontSize  = tier === 1 ? 14 : (tier === 2 ? 12 : 11)
          const fontStyle = highlighted ? 'bold' : (tier === 1 ? 'bold' : 'normal')
          // SEQ=10192 가독성 강화: L3 opacity 0.85 → 0.95 (식별성 보장)
          const labelOpacity = tier === 3 ? 0.95 : 1
          // SEQ=10192 가독성 강화: 전 도시 흰 stroke 적용 (L1=1.5 / L2=1.0 / L3=0.7).
          // 노선·USA 지도 배경 겹침 시 라벨 식별 향상. fillAfterStrokeEnabled=true 와 함께 동작.
          const labelStrokeWidth = tier === 1 ? 1.5 : (tier === 2 ? 1.0 : 0.7)
          // SEQ=10186 ② — hover 시 radius +1, stroke +1px (인접 lane ring 동반)
          const baseRadius = tier === 1 ? 9 : (tier === 2 ? 8 : 7)
          const nodeRadius = isHovered ? baseRadius + 1 : baseRadius
          const baseStroke = tier === 1 ? 2.5 : 2
          const nodeStroke = isHovered ? baseStroke + 1 : baseStroke
          return (
            <Group key={`city-${c.cityId}`}>
              {highlighted && (
                <Circle
                  x={p.x}
                  y={p.y}
                  radius={14}
                  fill="rgba(255, 235, 59, 0.35)"
                  stroke="#FFEB3B"
                  strokeWidth={2}
                  listening={false}
                />
              )}
              <Circle
                x={p.x}
                y={p.y}
                radius={nodeRadius}
                fill="#FFFFFF"
                stroke={isHovered ? '#5C8A2D' : '#212121'}
                strokeWidth={nodeStroke}
                onMouseEnter={() => setHoveredCityId(c.cityId)}
                onMouseLeave={() => setHoveredCityId(prev => prev === c.cityId ? null : prev)}
                onTouchStart={() => {
                  clearCityHoldTimer()
                  cityHoldTimerRef.current = window.setTimeout(() => {
                    setHoveredCityId(c.cityId)
                    cityHoldTimerRef.current = null
                  }, 400)
                }}
                onTouchEnd={() => {
                  clearCityHoldTimer()
                  // 모바일 tap 은 release 시 자동 해제 (다음 tap 까지 유지하면 산만)
                  setHoveredCityId(prev => prev === c.cityId ? null : prev)
                }}
                onTouchMove={() => clearCityHoldTimer()}
                onClick={() => onCityClick && onCityClick(c.cityId)}
                onTap={()  => onCityClick && onCityClick(c.cityId)}
              />
              {/* catch-A fix (SEQ=10195): LABEL_ANCHOR_OVERRIDE 적용 — Oklahoma City 등 라벨 위치 보정 */}
              {(() => {
                const override = LABEL_ANCHOR_OVERRIDE.get(c.cityId)
                const lx = override ? p.x + override.dx : p.x + (tier === 1 ? 12 : 10)
                const ly = override ? p.y + override.dy : p.y - (tier === 1 ? 7 : 6)
                return (
                  <Text
                    x={lx}
                    y={ly}
                    text={p.name}
                    fontFamily='"Noto Sans KR", "Segoe UI", system-ui, sans-serif'
                    fontSize={fontSize}
                    fontStyle={fontStyle}
                    fill="#2C2C2C"
                    stroke="#FFFFFF"
                    strokeWidth={labelStrokeWidth}
                    fillAfterStrokeEnabled={true}
                    opacity={labelOpacity}
                    listening={false}
                  />
                )
              })()}
            </Group>
          )
        })}

        {/* ── ④ SEQ=10200 STEP=1 (catch #11) — 최근 점유 `+N` 점수 popover (1.5초) ───
              노선 중심점에서 위로 fly + fade. progress 0→1 동안:
                y offset 0 → -34 (위로 떠오름)
                opacity 1.0 → 0.0
              fontSize 18 (가독성 우선), 좌석 색 fill + 흰 stroke, listening=false. */}
        {recentClaim && recentClaim.scoreDelta > 0 && recentClaimProgress < 1 && (() => {
          const r = routes.find(rr => rr.routeId === recentClaim.routeId && rr.laneNo === recentClaim.laneNo)
          if (!r) return null
          const a = cityPixels.get(r.cityAId)
          const b = cityPixels.get(r.cityBId)
          if (!a || !b) return null
          const laneOffset = r.isDouble ? (r.laneNo === 1 ? 7 : -7) : 0
          // 수직 단위 벡터 (lane offset 적용 노선 중심점)
          const dx = b.x - a.x
          const dy = b.y - a.y
          const len = Math.hypot(dx, dy) || 1
          const px = -(dy / len)
          const py =  (dx / len)
          const cx = (a.x + b.x) / 2 + px * laneOffset
          const cy = (a.y + b.y) / 2 + py * laneOffset
          const t = recentClaimProgress
          const popY = cy - 34 * t
          const popOpacity = 1 - t * t   // ease-in fade (마지막 더 빠르게)
          const seatColor = PLAYER_COLOR_HEX[recentClaim.ownerColor] ?? '#FFEB3B'
          return (
            <Text
              key={`recent-pop-${recentClaim.routeId}-${recentClaim.laneNo}-${recentClaim.ts}`}
              x={cx - 30}
              y={popY - 9}
              width={60}
              align="center"
              text={`+${recentClaim.scoreDelta}`}
              fontFamily='"Noto Sans KR", "Segoe UI", system-ui, sans-serif'
              fontSize={18}
              fontStyle="bold"
              fill={seatColor}
              stroke="#FFFFFF"
              strokeWidth={2}
              fillAfterStrokeEnabled={true}
              opacity={popOpacity}
              listening={false}
            />
          )
        })()}

        {/* Hover 툴팁 (간단 텍스트) */}
        {hoveredKey && (() => {
          const [rid, ln] = hoveredKey.split(':').map(Number)
          const r = routes.find(rr => rr.routeId === rid && rr.laneNo === ln)
          if (!r) return null
          const a = cityPixels.get(r.cityAId)
          const b = cityPixels.get(r.cityBId)
          if (!a || !b) return null
          const mx = (a.x + b.x) / 2
          const my = (a.y + b.y) / 2 - 16
          return (
            <Text
              key="hover-tip"
              x={mx - 50}
              y={my}
              width={100}
              align="center"
              text={`${a.name}↔${b.name}\n${r.color} ${r.length}칸`}
              fontSize={10}
              fill="#212121"
              listening={false}
            />
          )
        })()}
      </Layer>
    </Stage>
    {/* SEQ=10211 STEP=8 (사용자 catch "핸드 탭 button 무반응"): JSX 조건부 렌더로 변경.
     *   STEP=7 position: fixed 후 일부 brower에서 wrap display:none 잔재로 fixed children이 lingering →
     *   핸드 탭 우측 패널 button overlay 클릭 차단 의심. showZoomControls=false 시 React tree 자체 unmount. */}
    {showZoomControls && (
      <div className="ttr-board-zoom-controls" role="group" aria-label="보드 줌 조작">
        <button type="button" onClick={zoomIn}    aria-label="확대"     title="확대 (+)">＋</button>
        <button type="button" onClick={zoomReset} aria-label="줌 초기화" title={`초기화 (현재 ${Math.round(scale * 100)}%)`}>⊙</button>
        <button type="button" onClick={zoomOut}   aria-label="축소"     title="축소 (−)">－</button>
      </div>
    )}
    </div>
  )
}

export default TtrBoard
